import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';

/**
 * The "points per 1 €" field exactly as designed for the setup wizard:
 * inline ratio input with the LOMY-coin suffix, allowed-range hint,
 * validation errors and the live example calculator (customer-visible
 * floored points vs precise accumulated points). Shared between the
 * setup wizard and the loyalty-program edit page so the two never drift.
 */
@Component({
  selector: 'app-points-ratio-field',
  templateUrl: './points-ratio-field.component.html',
  styleUrls: ['./points-ratio-field.component.scss'],
})
export class PointsRatioFieldComponent {
  /** The `pointsPerLomyCoin` form control (validators owned by the parent form). */
  @Input() control!: FormControl;

  /** Editable sample amount driving the live calculator. */
  public sampleAmount: number | string = 24.8;

  public get showRequired(): boolean {
    return this.showError('required');
  }
  public get showPattern(): boolean {
    return this.showError('pattern');
  }
  public get showMin(): boolean {
    return this.showError('min');
  }
  public get showMax(): boolean {
    return this.showError('max');
  }

  private showError(code: string): boolean {
    return (
      !!this.control &&
      this.control.hasError(code) &&
      (this.control.touched || this.control.dirty)
    );
  }

  public get isInvalid(): boolean {
    return (
      this.showRequired || this.showPattern || this.showMin || this.showMax
    );
  }

  /** Ratio as a non-negative number, or NaN when invalid/empty. */
  private get ratioValue(): number {
    const raw = this.control?.value;
    const value = parseFloat(String(raw ?? '').replace(',', '.'));
    return isNaN(value) || value < 0 ? NaN : value;
  }

  /** Sample receipt amount as a non-negative number, or NaN when invalid/empty. */
  private get sampleValue(): number {
    const value = parseFloat(String(this.sampleAmount ?? '').replace(',', '.'));
    return isNaN(value) || value < 0 ? NaN : value;
  }

  /** What the customer sees — whole LOMY points (floored). "—" when inputs invalid. */
  public get awardedPoints(): string {
    const ratio = this.ratioValue;
    const amount = this.sampleValue;
    if (isNaN(ratio) || isNaN(amount)) return '—';
    return String(Math.floor(amount * ratio));
  }

  /** Precise accumulated points (max 2 decimals). "—" when invalid. */
  public get accumulatedPoints(): string {
    const ratio = this.ratioValue;
    const amount = this.sampleValue;
    if (isNaN(ratio) || isNaN(amount)) return '—';
    return String(Math.round(amount * ratio * 100) / 100);
  }
}
